import type { TextSelection } from '@/types'

export class SelectionsGallery {
  private container: HTMLElement
  private selections: TextSelection[] = []
  private draggedElement: HTMLElement | null = null
  private onSelectionReorder?: (selections: TextSelection[]) => void
  private onSelectionRemove?: (id: string) => void
  private onSelectionNavigate?: (id: string) => void
  private onRetryThumbnail?: (id: string) => void
  private documentType: string = 'pdf' // Default to PDF for backward compatibility

  private getPDFName?: (pdfId: string) => string

  constructor(
    container: HTMLElement,
    options: {
      onSelectionReorder?: (selections: TextSelection[]) => void
      onSelectionRemove?: (id: string) => void
      onSelectionNavigate?: (id: string) => void
      onRetryThumbnail?: (id: string) => void
      getPDFName?: (pdfId: string) => string
      documentType?: string
    } = {}
  ) {
    this.container = container
    this.onSelectionReorder = options.onSelectionReorder
    this.onSelectionRemove = options.onSelectionRemove
    this.onSelectionNavigate = options.onSelectionNavigate
    this.onRetryThumbnail = options.onRetryThumbnail
    this.getPDFName = options.getPDFName
    this.documentType = options.documentType || 'pdf'
    this.init()
  }

  private init(): void {
    this.container.className = 'selections-gallery'
    this.render()
    this.setupEventListeners()
    console.log('🖼️ SelectionsGallery initialized')
  }

  /**
   * Get appropriate empty state message based on document type
   */
  private getEmptyStateMessage(): string {
    switch (this.documentType.toLowerCase()) {
      case 'image':
        return 'Click and drag on the image to create selections'
      case 'pdf':
        return 'Click and drag on the PDF to create selections'
      case 'manual':
        return 'Add manual text entries to create selections'
      default:
        return 'Click and drag on the document to create selections'
    }
  }

  public getSelections(): TextSelection[] {
    return this.selections
  }


  private render(): void {
    this.container.innerHTML = `
      <div class="gallery-header">
        <h3 class="text-lg font-semibold text-gray-800 flex items-center">
          <span class="mr-2">🖼️</span>
          Text Selections
          <span id="selection-count" class="ml-2 text-sm font-normal text-gray-500">(0)</span>
        </h3>

      </div>
      
      <div id="gallery-container" class="gallery-grid">
        <div class="empty-state">
          <div class="text-gray-400 text-center py-8">
            <div class="text-4xl mb-2">📋</div>
            <div class="text-sm">No selections yet</div>
            <div class="text-xs text-gray-500 mt-1">${this.getEmptyStateMessage()}</div>
          </div>
        </div>
      </div>
    `

    // Add event listeners
  }

  updateSelections(selections: TextSelection[]): void {
    console.log(`🖼️ SelectionsGallery.updateSelections called with ${selections.length} selections`)
    console.log(`🖼️ Previous selections: ${this.selections.length}, New selections: ${selections.length}`)
    console.log(`🖼️ Selection IDs: [${selections.map(s => s.id.startsWith('temp_') ? s.id.substring(0, 12) + '...' : s.id.substring(0, 8) + '...').join(', ')}]`)
    
    this.selections = [...selections]
    this.renderGallery()
  }

  private renderGallery(): void {
    const container = this.container.querySelector('#gallery-container')
    const countElement = this.container.querySelector('#selection-count')

    if (!container) return

    // Update count
    if (countElement) {
      countElement.textContent = `(${this.selections.length})`
    }

    if (this.selections.length === 0) {
      container.innerHTML = `
        <div class="empty-state">
          <div class="text-gray-400 text-center py-8">
            <div class="text-4xl mb-2">📋</div>
            <div class="text-sm">No selections yet</div>
            <div class="text-xs text-gray-500 mt-1">${this.getEmptyStateMessage()}</div>
          </div>
        </div>
      `
      return
    }

    // Render selection cards with drag-and-drop
    container.innerHTML = this.selections.map((selection, index) => `
      <div 
        class="selection-card" 
        data-selection-id="${selection.id}"
        draggable="true"
      >
        <!-- Drop zone before -->
        <div class="drop-zone drop-zone-before" data-index="${index}"></div>
        
        <!-- Selection content -->
        <div class="selection-content">
          <!-- Selection number badge -->
          <div class="selection-number">${index + 1}</div>
          
          <!-- Remove button -->
          <button class="remove-btn" data-selection-id="${selection.id}" title="Remove selection">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
            </svg>
          </button>
          
          <!-- Snippet Preview -->
          <div class="selection-thumbnail" data-selection-id="${selection.id}">
            ${selection.thumbnail_data && selection.thumbnail_data !== 'error' && selection.thumbnail_data !== 'loading' && selection.thumbnail_data.length > 10 ? `
              <img 
                src="${selection.thumbnail_data}" 
                alt="Text Snippet ${index + 1}"
                class="thumbnail-image"
                title="Click to view at full resolution or navigate to PDF"
                data-full-image="${selection.thumbnail_data}"
              />
            ` : selection.thumbnail_data === 'loading' || (selection.id && selection.id.startsWith('temp_')) ? `
              <div class="thumbnail-placeholder">
                <div class="flex flex-col items-center justify-center py-4">
                  <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mb-2"></div>
                  <div class="text-blue-600 text-xs font-medium">Loading snippet...</div>
                  <div class="text-gray-400 text-xs mt-1">Capturing high-quality image</div>
                </div>
              </div>
            ` : selection.thumbnail_data === 'error' || selection.thumbnail_data === '' ? `
              <div class="thumbnail-placeholder">
                <div class="text-red-400 text-xs">⚠️ Thumbnail not available</div>
                <div class="text-gray-400 text-xs mt-1">Click 'Go' to view in ${selection.document_type === 'image' ? 'image' : 'PDF'}</div>
                <button class="retry-thumbnail-btn mt-2 px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-600 rounded" 
                        data-selection-id="${selection.id}">
                  🔄 Reload Snippet
                </button>
              </div>
            ` : `
              <div class="thumbnail-placeholder">
                <div class="text-orange-400 text-xs">⚠️ Thumbnail missing</div>
                <div class="text-gray-400 text-xs mt-1">Click 'Go' to view in ${selection.document_type === 'image' ? 'image' : 'PDF'}</div>
                <button class="retry-thumbnail-btn mt-2 px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-600 rounded" 
                        data-selection-id="${selection.id}">
                  🔄 Generate Snippet
                </button>
              </div>
            `}
          </div>
          
          <!-- Selection info -->
          <div class="selection-info">
            <div class="selection-meta">
              <span class="meta-item">${selection.document_type === 'image' ? '🖼️ Image Selection' : `📄 Page ${selection.page_number}`}</span>
              <span class="meta-item">${selection.document_type === 'image' ? '🖼️' : '📄'} ${this.getPDFName && selection.document_id ? this.getPDFName(selection.document_id) : (selection.document_type === 'image' ? 'Unknown Image' : 'Unknown PDF')}</span>
            </div>
            <div class="selection-actions">
              <button 
                class="action-btn navigate-btn" 
                data-selection-id="${selection.id}"
                title="Go to this selection"
              >
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                </svg>
                Go
              </button>
            </div>
          </div>
          
          <!-- Drag handle -->
          <div class="drag-handle" title="Drag to reorder">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
            </svg>
          </div>
        </div>
        
        <!-- Drop zone after (only for last item) -->
        ${index === this.selections.length - 1 ? `<div class="drop-zone drop-zone-after" data-index="${index + 1}"></div>` : ''}
      </div>
    `).join('')

    // Add drop zone at the end if no selections
    if (this.selections.length > 0) {
      const lastDropZone = container.querySelector('.drop-zone-after')
      if (!lastDropZone) {
        const endDropZone = document.createElement('div')
        endDropZone.className = 'drop-zone drop-zone-end'
        endDropZone.dataset.index = this.selections.length.toString()
        container.appendChild(endDropZone)
      }
    }

    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    // Listen for refresh events to update the gallery
    window.addEventListener('refresh-selections-gallery', (event: any) => {
      console.log('🔄 SelectionsGallery received refresh event:', event.detail)
      const { selections } = event.detail
      if (selections && selections.length > 0) {
        console.log('🔄 Updating selections gallery with', selections.length, 'selections')
        this.updateSelections(selections)
      }
    })

    const container = this.container.querySelector('#gallery-container')
    if (!container) return

    // Remove button handlers
    container.querySelectorAll('.remove-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const selectionId = (e.currentTarget as HTMLElement).dataset.selectionId
        if (selectionId && this.onSelectionRemove) {
          this.onSelectionRemove(selectionId)
        }
      })
    })

    // Navigate button handlers
    container.querySelectorAll('.navigate-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const selectionId = (e.currentTarget as HTMLElement).dataset.selectionId
        if (selectionId && this.onSelectionNavigate) {
          this.onSelectionNavigate(selectionId)
        }
      })
    })

    // Retry thumbnail button handlers
    container.querySelectorAll('.retry-thumbnail-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const selectionId = (e.currentTarget as HTMLElement).dataset.selectionId
        if (selectionId && this.onRetryThumbnail) {
          this.onRetryThumbnail(selectionId)
        }
      })
    })

    // Thumbnail click handlers
    container.querySelectorAll('.selection-thumbnail').forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        e.stopPropagation()
        const selectionId = (e.currentTarget as HTMLElement).dataset.selectionId
        const target = e.target as HTMLElement
        
        // If user clicked on the image, show full-size view
        if (target.tagName === 'IMG' && target.hasAttribute('data-full-image')) {
          this.showFullSizeImage(target.getAttribute('data-full-image')!, selectionId!)
        } 
        // Otherwise navigate to the selection
        else if (selectionId && this.onSelectionNavigate) {
          this.onSelectionNavigate(selectionId)
        }
      })
    })

    // Drag and drop handlers
    this.setupDragAndDrop()
  }

  private setupDragAndDrop(): void {
    const selectionCards = this.container.querySelectorAll('.selection-card')
    const dropZones = this.container.querySelectorAll('.drop-zone')

    // Selection card drag handlers
    selectionCards.forEach(card => {
      card.addEventListener('dragstart', (e) => {
        const dragEvent = e as DragEvent
        this.draggedElement = e.currentTarget as HTMLElement
        this.draggedElement.classList.add('dragging')
        
        if (dragEvent.dataTransfer) {
          dragEvent.dataTransfer.effectAllowed = 'move'
          dragEvent.dataTransfer.setData('text/html', this.draggedElement.outerHTML)
        }
      })

      card.addEventListener('dragend', () => {
        if (this.draggedElement) {
          this.draggedElement.classList.remove('dragging')
          this.draggedElement = null
        }
      })
    })

    // Drop zone handlers
    dropZones.forEach(zone => {
      zone.addEventListener('dragover', (e) => {
        const dragEvent = e as DragEvent
        e.preventDefault()
        if (dragEvent.dataTransfer) {
          dragEvent.dataTransfer.dropEffect = 'move'
        }
        zone.classList.add('drag-over')
      })

      zone.addEventListener('dragleave', () => {
        zone.classList.remove('drag-over')
      })

      zone.addEventListener('drop', (e) => {
        e.preventDefault()
        zone.classList.remove('drag-over')
        
        if (this.draggedElement) {
          const targetIndex = parseInt((e.currentTarget as HTMLElement).dataset.index || '0')
          const sourceId = this.draggedElement.dataset.selectionId
          
          if (sourceId) {
            this.reorderSelection(sourceId, targetIndex)
          }
        }
      })
    })
  }

  private reorderSelection(selectionId: string, targetIndex: number): void {
    const sourceIndex = this.selections.findIndex(s => s.id === selectionId)
    if (sourceIndex === -1) return

    // Create new array with reordered selections
    const newSelections = [...this.selections]
    const [movedSelection] = newSelections.splice(sourceIndex, 1)
    
    // Adjust target index if moving forward
    const adjustedIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex
    newSelections.splice(adjustedIndex, 0, movedSelection)

    this.selections = newSelections
    this.renderGallery()

    // Notify parent of reorder
    if (this.onSelectionReorder) {
      this.onSelectionReorder(this.selections)
    }

    console.log(`📦 Reordered selection ${selectionId} from ${sourceIndex} to ${adjustedIndex}`)
  }



  private showFullSizeImage(imageSrc: string, selectionId: string): void {
    // Create modal overlay
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4'
    modal.style.zIndex = '9999'
    
    // Create modal content
    modal.innerHTML = `
      <div class="bg-white rounded-lg max-w-4xl max-h-full overflow-hidden shadow-2xl">
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-lg font-semibold">Snippet Preview - Full Resolution</h3>
          <button class="close-modal text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div class="p-4 overflow-auto max-h-96">
          <img 
            src="${imageSrc}" 
            alt="Full-size snippet preview" 
            class="max-w-full h-auto block mx-auto"
            style="image-rendering: pixelated; image-rendering: -webkit-optimize-contrast; image-rendering: crisp-edges;"
          />
        </div>
        <div class="p-4 border-t bg-gray-50 flex justify-between">
          <div class="text-sm text-gray-600">
            Click outside or press ESC to close
          </div>
          <button class="btn btn-primary navigate-from-modal" data-selection-id="${selectionId}">
            Go to PDF Location
          </button>
        </div>
      </div>
    `
    
    // Add to body
    document.body.appendChild(modal)
    
    // Event listeners
    const closeModal = () => {
      document.body.removeChild(modal)
      document.removeEventListener('keydown', handleKeyDown)
    }
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
      }
    }
    
    // Close on click outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal()
      }
    })
    
    // Close button
    modal.querySelector('.close-modal')?.addEventListener('click', closeModal)
    
    // Navigate button
    modal.querySelector('.navigate-from-modal')?.addEventListener('click', () => {
      closeModal()
      if (this.onSelectionNavigate) {
        this.onSelectionNavigate(selectionId)
      }
    })
    
    // ESC key
    document.addEventListener('keydown', handleKeyDown)
    
    console.log(`🔍 Showing full-size snippet preview for selection: ${selectionId}`)
  }
} 